import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/MCQ.scss";

const TypewriterText = ({ text }) => {
  const [displayedText, setDisplayedText] = useState("");
  const indexRef = useRef(0);

  useEffect(() => {
    setDisplayedText("");
    indexRef.current = -1;

    if (!text) return;

    const intervalId = setInterval(() => {
      setDisplayedText((prev) => prev + text.charAt(indexRef.current));
      indexRef.current += 1;

      if (indexRef.current >= text.length) {
        clearInterval(intervalId);
      }
    }, 20);

    return () => clearInterval(intervalId);
  }, [text]);

  return <span>{displayedText}</span>;
};

const MCQ = ({
  questions = [],
  current: propCurrent,
  setCurrent: propSetCurrent,
  answers: propAnswers,
  setAnswers: propSetAnswers,
  onNext,
  onPrev,
  mode,
  isAdmin,
  questionId,
}) => {
  const [searchParams] = useSearchParams();
  const isReviewParam = searchParams.get("review") === "true";
  const parsedQuestions = useMemo(() => {
    if (!questions) return [];

    let inputList = questions;

    if (typeof inputList === "string") {
      try {
        inputList = JSON.parse(inputList);
      } catch (e) {
        console.error("Error parsing questions string:", e);
        return [];
      }
    }

    if (!Array.isArray(inputList)) {
      if (inputList.questions && Array.isArray(inputList.questions)) {
        inputList = inputList.questions;
      } else {
        inputList = [inputList];
      }
    }

    if (inputList.length === 0) return [];

    if (
      inputList[0] &&
      typeof inputList[0] === "object" &&
      "content" in inputList[0]
    ) {
      try {
        return inputList.flatMap((item) => {
          if (item.content) {
            let parsedContent;
            if (typeof item.content === "string") {
              try {
                parsedContent = JSON.parse(item.content);
              } catch (e) {
                console.error("Error parsing content string:", e);
                return [];
              }
            } else if (typeof item.content === "object") {
              parsedContent = item.content;
            }

            if (
              parsedContent?.questions &&
              Array.isArray(parsedContent.questions)
            ) {
              return parsedContent.questions;
            }
            if (Array.isArray(parsedContent)) {
              return parsedContent;
            }
            return parsedContent ? [parsedContent] : [];
          }
          return [];
        });
      } catch (e) {
        console.error("Error processing nested questions:", e);
        return [];
      }
    }

    if (typeof inputList[0] === "string") {
      try {
        return inputList.map((q) =>
          typeof q === "string" ? JSON.parse(q) : q,
        );
      } catch (e) {
        console.error("Error parsing questions:", e);
        return inputList;
      }
    }

    return inputList;
  }, [questions]);

  const [internalCurrent, setInternalCurrent] = useState(0);
  const [internalAnswers, setInternalAnswers] = useState({});
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const isControlled = propCurrent !== undefined && propAnswers !== undefined;

  const current = isControlled ? propCurrent : internalCurrent;
  const answers = isControlled ? propAnswers : internalAnswers;

  const setAnswers = (val) => {
    if (isControlled) {
      propSetAnswers(val);
    } else {
      setInternalAnswers(val);
    }
  };

  const currentQ = parsedQuestions[current];

  const score = useMemo(() => {
    return parsedQuestions.reduce((acc, q) => {
      const questionId = q?.question_id || q?.question;
      const ans = answers[questionId];
      const correctAnswer = q?.correct_answer || q?.answer;
      return acc + (ans && ans === correctAnswer ? 1 : 0);
    }, 0);
  }, [answers, parsedQuestions]);

  const handleSelect = (optionKey) => {
    if (!currentQ) return;
    const finalQuestionId =
      questionId || currentQ.question_id || currentQ.question;
    const showFeedback = mode === "review" || isAdmin;

    if (showFeedback) return;

    setAnswers((prev) => ({ ...prev, [finalQuestionId]: optionKey }));

    if (current < parsedQuestions.length - 1) {
      startAutoAdvance();
    }
  };

  const clearTimers = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const startAutoAdvance = () => {
    clearTimers();
    intervalRef.current = setInterval(() => {}, 50);
  };

  useEffect(() => {
    return () => clearTimers();
  }, []);

  useEffect(() => {
    clearTimers();
  }, [current]);

  if (!parsedQuestions || parsedQuestions.length === 0) {
    return <div className="mcq-empty">No questions provided.</div>;
  }

  const finalQuestionId =
    questionId || currentQ?.question_id || currentQ?.question;
  const selected = answers[finalQuestionId];
  const showFeedback = mode === "review" || isAdmin;
  const correctAnswer = currentQ?.correct_answer || currentQ?.answer;

  return (
    <div className="mcq-container">
      <div className="mcq-wrapper">
        <div className="mcq-content custom-scrollbar">
          <div className="mcq-card">
            <div className="mcq-question" aria-live="polite">
              {currentQ?.question}
            </div>

            <div className="mcq-options" role="group" aria-label="Options">
              {currentQ?.options &&
                (Array.isArray(currentQ?.options)
                  ? currentQ?.options?.map((optionObj, index) => {
                      const key = Object.keys(optionObj)[0];
                      const label = optionObj[key];

                      const normalizedKey = String(key).toUpperCase();
                      const normalizedSelected = selected
                        ? String(selected).toUpperCase()
                        : null;
                      const normalizedCorrect =
                        String(correctAnswer).toUpperCase();
                      const normalizedLabel = String(label).toUpperCase();

                      const isSelected = !!normalizedSelected && (normalizedSelected === normalizedKey || normalizedSelected === normalizedLabel);
                      const isCorrect =
                        showFeedback && (normalizedKey === normalizedCorrect || normalizedCorrect === normalizedLabel);
                      const isIncorrect =
                        showFeedback &&
                        isSelected &&
                        !isCorrect;

                      return (
                        <button
                          key={key}
                          className={[
                            "mcq-option",
                            isSelected ? "selected" : "",
                            isCorrect ? "correct" : "",
                            isIncorrect ? "incorrect" : "",
                          ].join(" ")}
                          onClick={() => handleSelect(key)}
                          disabled={showFeedback}
                          aria-pressed={isSelected}
                        >
                          <span
                            className={"mcq-option-key"}
                            style={{
                              borderColor: isIncorrect
                                ? "var(--incorrect-red)"
                                : isSelected
                                  ? "var(--selected-blue-color)"
                                  : isCorrect
                                    ? "var(--color-primary)"
                                    : "",
                            }}
                          >
                            {key}
                          </span>
                          <span className="mcq-option-label">{label}</span>
                        </button>
                      );
                    })
                  : Object.entries(currentQ.options)?.map(([key, label]) => {
                      const normalizedKey = String(key).toUpperCase();
                      const normalizedSelected = selected
                        ? String(selected).toUpperCase()
                        : null;
                      const normalizedCorrect =
                        String(correctAnswer).toUpperCase();
                      const normalizedLabel = String(label).toUpperCase();

                      const isSelected = !!normalizedSelected && (normalizedSelected === normalizedKey || normalizedSelected === normalizedLabel);
                      const isCorrect =
                        showFeedback && (normalizedKey === normalizedCorrect || normalizedCorrect === normalizedLabel);
                      const isIncorrect =
                        showFeedback &&
                        isSelected &&
                        !isCorrect;

                      return (
                        <button
                          key={key}
                          className={[
                            "mcq-option",
                            isSelected ? "selected" : "",
                            isCorrect ? "correct" : "",
                            isIncorrect ? "incorrect" : "",
                          ].join(" ")}
                          onClick={() => handleSelect(key)}
                          disabled={showFeedback}
                          aria-pressed={isSelected}
                        >
                          <span
                            className="mcq-option-key"
                            style={{
                              borderColor: isIncorrect
                                ? "var(--incorrect-red)"
                                : isSelected
                                  ? "var(--selected-blue-color)"
                                  : isCorrect
                                    ? "var(--color-primary)"
                                    : "",
                            }}
                          >
                            {key}
                          </span>
                          <span className="mcq-option-label">{label}</span>
                        </button>
                      );
                    }))}
            </div>

            {showFeedback &&
              currentQ.explanation &&
              (isReviewParam || mode === "review" || isAdmin) && (
                <div className="mcq-explanation">
                  <strong>Explanation:</strong>
                  {/* <TypewriterText text={currentQ.explanation} /> */}
                  {currentQ.explanation}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MCQ;
